/**
 * What an admin can do to ONE attempt, from the screen where they are looking
 * at it.
 *
 * These actions existed only on the live monitor, and only for attempts that
 * were still running — so the moment a sitting ended there was no way to reset
 * or reopen it from anywhere in the app. That is exactly backwards: you decide
 * a sitting is a write-off by reviewing it, which is this screen.
 *
 * Which actions are offered is driven by the attempt's status, because every
 * one of them is refused server-side for the wrong status and an enabled
 * button that always 409s is worse than no button.
 */
import { useState } from "react";
import { Clock, RotateCcw, Square, Trash2, Undo2 } from "lucide-react";
import { Button, Dialog, Input, useToast } from "@/components/ui";
import type { AttemptStatus } from "@exam/shared";
import {
  extendAttempt,
  forceSubmitAttempt,
  reopenAttempt,
  resetAttempt,
} from "../data/client";
import { formatDuration } from "../lib/format";

type Action = "extend" | "force_submit" | "reopen" | "reset";

interface Pending {
  action: Action;
  minutes: string;
  note: string;
}

/** Everything the confirm dialog has to say, per action. */
const COPY: Record<
  Action,
  {
    title: string;
    verb: string;
    danger: boolean;
    minutesLabel?: string;
    body: string;
  }
> = {
  extend: {
    title: "Extend this attempt",
    verb: "Extend",
    danger: false,
    minutesLabel: "Minutes to add (negative takes time away)",
    body: "Adds time to this attempt's clock only. Nobody else sitting this exam is affected.",
  },
  force_submit: {
    title: "End this attempt now",
    verb: "End it now",
    danger: true,
    body: "The paper is graded exactly as a normal submit would grade it, and closed immediately. The student cannot go back in.",
  },
  reopen: {
    title: "Reopen this attempt",
    verb: "Reopen",
    danger: false,
    minutesLabel: "Extra minutes to give them",
    body: "Puts the attempt back in progress. Every answer already given is kept. If the original deadline has passed, enough time is granted to make it usable and the amount is reported back.",
  },
  reset: {
    title: "Reset this attempt",
    verb: "Reset it",
    danger: true,
    body: "PERMANENTLY deletes this sitting's answers, score, proctor events and every recording it produced — the video files too. The attempt returns to not-started and the student can sit it again from scratch. To keep this sitting and give them another go, grant a retake from the exam instead.",
  },
};

export function AttemptActions({
  attemptId,
  studentId,
  status,
  extraTimeMin,
  onDone,
}: {
  attemptId: string;
  studentId: string;
  status: AttemptStatus;
  extraTimeMin: number;
  onDone: () => void;
}) {
  const { toast } = useToast();
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);

  const running = status === "in_progress";
  const finished = status === "submitted" || status === "auto_submitted";
  const started = status !== "not_started";

  const open = (action: Action) =>
    setPending({
      action,
      minutes: action === "extend" ? "10" : "15",
      note: "",
    });

  const copy = pending ? COPY[pending.action] : null;
  const minutes = Number(pending?.minutes ?? "");
  const minutesValid =
    !copy?.minutesLabel ||
    (Number.isInteger(minutes) &&
      Math.abs(minutes) <= 600 &&
      (pending?.action !== "extend" || minutes !== 0));

  const run = async () => {
    if (!pending) return;
    const note = pending.note.trim() === "" ? null : pending.note.trim();
    setBusy(true);
    try {
      switch (pending.action) {
        case "extend": {
          const res = await extendAttempt(attemptId, minutes, note);
          toast({
            tone: "success",
            title: `Extended by ${minutes} minutes.`,
            // Every field on the response is optional in the contract, so a
            // `?? 0` here would report "0s left" for a successful extension.
            description:
              res.remaining_ms === undefined
                ? undefined
                : `${formatDuration(res.remaining_ms)} left.`,
          });
          break;
        }
        case "force_submit": {
          const res = await forceSubmitAttempt(attemptId, note);
          toast({
            tone: "success",
            title: "Attempt ended and graded.",
            description:
              res.score === undefined || res.max_score === undefined
                ? undefined
                : `Scored ${res.score} of ${res.max_score}.`,
          });
          break;
        }
        case "reopen": {
          const res = await reopenAttempt(attemptId, minutes, note);
          toast({
            tone: "success",
            title: "Attempt reopened.",
            description: `${res.granted_extra_minutes ?? minutes} extra minutes granted.`,
          });
          break;
        }
        case "reset": {
          // The status is sent as the caller understood it, so a screen left
          // open while the attempt moved on is refused rather than wiping
          // something the admin was not looking at.
          const res = await resetAttempt(attemptId, note, {
            studentId,
            status,
          });
          toast({
            tone: "success",
            title: "Attempt reset.",
            // "0 recording files deleted" straight after a purge that deleted
            // everything is worse than saying nothing.
            description:
              res.media_objects_deleted === undefined
                ? undefined
                : `${res.media_objects_deleted} recording files deleted.`,
          });
          break;
        }
      }
      setPending(null);
      onDone();
    } catch (err) {
      toast({
        tone: "error",
        title: "That did not go through.",
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <span className="flex flex-wrap items-center gap-1.5">
        {running ? (
          <>
            <Button size="sm" icon={<Clock />} onClick={() => open("extend")}>
              Extend
            </Button>
            <Button
              size="sm"
              icon={<Square />}
              onClick={() => open("force_submit")}
            >
              End now
            </Button>
          </>
        ) : null}

        {finished ? (
          <Button size="sm" icon={<Undo2 />} onClick={() => open("reopen")}>
            Reopen
          </Button>
        ) : null}

        {started ? (
          <Button size="sm" icon={<Trash2 />} onClick={() => open("reset")}>
            Reset
          </Button>
        ) : null}
      </span>

      <Dialog
        busy={busy}
        open={pending !== null}
        onClose={() => setPending(null)}
        title={copy?.title ?? ""}
        footer={
          <>
            <Button onClick={() => setPending(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant={copy?.danger ? "danger" : "primary"}
              disabled={busy || !minutesValid}
              icon={copy?.danger ? <RotateCcw /> : undefined}
              onClick={() => void run()}
            >
              {busy ? "Working…" : (copy?.verb ?? "Confirm")}
            </Button>
          </>
        }
      >
        {pending && copy ? (
          <div className="space-y-3">
            <p
              className={
                copy.danger
                  ? "rounded-md border border-violation/30 bg-violation-soft px-3 py-2 text-xs text-violation"
                  : "text-xs text-ink-muted"
              }
            >
              {copy.body}
            </p>

            {copy.minutesLabel ? (
              <Input
                label={copy.minutesLabel}
                type="number"
                value={pending.minutes}
                error={
                  minutesValid ? undefined : "Whole minutes, at most ±600."
                }
                hint={
                  pending.action === "extend" && extraTimeMin !== 0
                    ? `Already granted ${extraTimeMin > 0 ? "+" : ""}${extraTimeMin} min.`
                    : undefined
                }
                onChange={(e) =>
                  setPending({ ...pending, minutes: e.target.value })
                }
              />
            ) : null}

            <Input
              label="Why (kept on the record)"
              placeholder="Recording failed; letting them sit it again."
              value={pending.note}
              onChange={(e) => setPending({ ...pending, note: e.target.value })}
            />
          </div>
        ) : null}
      </Dialog>
    </>
  );
}
